<template>
  <v-dialog :value="value" max-width="520" persistent @input="$emit('input', $event)">
    <div class="novidades-export-modal">
      <div class="novidades-export-modal__header">
        <div class="d-flex align-center" style="gap: 10px;">
          <div class="novidades-export-modal__icon-wrap">
            <v-icon small color="#059669">fas fa-file-download</v-icon>
          </div>
          <div>
            <h2 class="novidades-export-modal__title">Exportar Novidades em PDF</h2>
            <p class="novidades-export-modal__subtitle">
              Gere um relatório formatado das melhorias da plataforma
            </p>
          </div>
        </div>
        <button type="button" class="novidades-export-modal__close" @click="close">
          <v-icon x-small>fas fa-times</v-icon>
        </button>
      </div>

      <div class="novidades-export-modal__body">
        <div class="novidades-export-modal__info">
          <v-icon x-small class="mr-2" color="#2563eb">fas fa-info-circle</v-icon>
          <div>
            <p class="font-weight-bold mb-0">Regra de exportação:</p>
            <p class="mb-0 mt-1">
              Para garantir a precisão e performance do relatório, o intervalo máximo
              permitido entre as datas é de <strong>30 dias</strong>.
            </p>
          </div>
        </div>

        <div class="novidades-export-modal__dates">
          <label>
            <span>Data Inicial</span>
            <input v-model="startDate" type="date" class="novidades-export-modal__date-input" />
          </label>
          <label>
            <span>Data Final</span>
            <input v-model="endDate" type="date" class="novidades-export-modal__date-input" />
          </label>
        </div>

        <div class="novidades-export-modal__presets">
          <span class="novidades-export-modal__presets-label">Atalhos rápidos:</span>
          <button type="button" @click="applyPreset(7)">Últimos 7 dias</button>
          <button type="button" @click="applyPreset(15)">Últimos 15 dias</button>
          <button type="button" @click="applyPreset(30)">30 dias (Máximo)</button>
        </div>

        <div v-if="loadingPreview" class="text-center py-3">
          <v-progress-circular indeterminate size="20" width="2" color="primary" />
        </div>

        <div v-else-if="isOverLimit" class="novidades-export-modal__error">
          <v-icon x-small class="mr-1">fas fa-exclamation-circle</v-icon>
          Intervalo selecionado: <strong>{{ diffDays }} dias</strong>. O limite máximo
          permitido é de 30 dias.
        </div>
        <div v-else-if="isNegative" class="novidades-export-modal__error">
          <v-icon x-small class="mr-1">fas fa-exclamation-circle</v-icon>
          A data final deve ser igual ou posterior à data inicial.
        </div>
        <div v-else-if="isValid && preview" class="novidades-export-modal__ok">
          <div class="d-flex justify-space-between align-center">
            <span>
              <v-icon x-small color="#059669" class="mr-1">fas fa-check-circle</v-icon>
              Período válido ({{ diffDays }} {{ diffDays === 1 ? "dia" : "dias" }})
            </span>
            <strong class="green--text text--darken-1">
              {{ preview.releases }}
              {{ preview.releases === 1 ? "release" : "releases" }}
            </strong>
          </div>
          <p class="mb-0 mt-2">
            Serão consolidadas
            <strong>{{ preview.melhorias }} melhorias</strong>
            documentadas no relatório oficial LifeApps.
          </p>
        </div>
      </div>

      <div class="novidades-export-modal__footer">
        <button type="button" class="novidades-export-modal__btn-cancel" @click="close">
          Cancelar
        </button>
        <button
          type="button"
          class="novidades-export-modal__btn-primary"
          :disabled="!isValid || generating"
          @click="confirm"
        >
          <v-icon x-small class="mr-1">fas fa-file-download</v-icon>
          {{ generating ? "Gerando PDF..." : "Gerar PDF" }}
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Api from "@/service/api-web";
import * as _ from "lodash";

function formatIsoDate(d) {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function defaultRange() {
  const end = new Date();
  const start = new Date(end.getTime() - 29 * 24 * 60 * 60 * 1000);
  return { start: formatIsoDate(start), end: formatIsoDate(end) };
}

export default {
  name: "ExportPeriodModal",
  props: {
    value: { type: Boolean, default: false },
    unidadeNome: { type: String, default: "" }
  },
  data: () => ({
    startDate: defaultRange().start,
    endDate: defaultRange().end,
    preview: null,
    loadingPreview: false,
    generating: false,
    previewTimer: null
  }),
  computed: {
    diffDays() {
      const startMs = new Date(`${this.startDate}T00:00:00`).getTime();
      const endMs = new Date(`${this.endDate}T00:00:00`).getTime();
      if (Number.isNaN(startMs) || Number.isNaN(endMs)) return 0;
      return Math.round((endMs - startMs) / (1000 * 60 * 60 * 24)) + 1;
    },
    isNegative() {
      return this.diffDays < 1;
    },
    isOverLimit() {
      return this.diffDays > 30;
    },
    isValid() {
      return !this.isNegative && !this.isOverLimit;
    }
  },
  watch: {
    value(open) {
      if (open) {
        const d = defaultRange();
        this.startDate = d.start;
        this.endDate = d.end;
        this.schedulePreview();
      }
    },
    startDate() {
      this.schedulePreview();
    },
    endDate() {
      this.schedulePreview();
    }
  },
  beforeDestroy() {
    if (this.previewTimer) clearTimeout(this.previewTimer);
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    applyPreset(days) {
      const end = new Date(`${this.endDate}T12:00:00`);
      const start = new Date(end.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
      this.startDate = formatIsoDate(start);
      this.endDate = formatIsoDate(end);
    },
    schedulePreview() {
      if (this.previewTimer) clearTimeout(this.previewTimer);
      this.previewTimer = setTimeout(() => {
        this.fetchPreview();
      }, 300);
    },
    fetchPreview() {
      if (!this.isValid) {
        this.preview = null;
        return;
      }
      this.loadingPreview = true;
      Api.v4.novidades
        .exportPreview(this.startDate, this.endDate)
        .then(resp => {
          this.preview = _.get(resp, "data.data", null);
        })
        .catch(() => {
          this.preview = null;
        })
        .finally(() => {
          this.loadingPreview = false;
        });
    },
    confirm() {
      if (!this.isValid || this.generating) return;
      this.generating = true;
      Api.v4.novidades.exportConsolidadoPdf(
          this.startDate,
          this.endDate,
          this.unidadeNome
        )
        .then(resp => {
          const blob = resp.data;
          const filename = `novidades-${this.startDate}_${this.endDate}.pdf`;
          this.$emit("pdf-ready", { blob, filename, title: "Pré-visualização do Relatório em PDF (A4)" });
          this.close();
        })
        .catch(() => {
          this.$emit("export-error", "Não foi possível gerar o PDF. Tente novamente.");
        })
        .finally(() => {
          this.generating = false;
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.novidades-export-modal {
  background: var(--np-surface, #fff);
  color: var(--np-text, #0f172a);
  border-radius: 16px;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 20px;
    border-bottom: 1px solid var(--np-border, #e2e8f0);
  }

  &__icon-wrap {
    width: 36px;
    height: 36px;
    border-radius: 12px;
    background: #ecfdf5;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    margin: 0;
  }

  &__subtitle {
    font-size: 12px;
    color: var(--np-text-muted, #64748b);
    margin: 2px 0 0;
  }

  &__close {
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 6px;
    border-radius: 8px;
    color: #94a3b8;
  }

  &__body {
    padding: 20px 24px;
    font-size: 12px;
  }

  &__info {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 14px;
    border-radius: 12px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #1e40af;
    margin-bottom: 16px;
  }

  &__dates {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 12px;

    label span {
      display: block;
      font-weight: 600;
      margin-bottom: 4px;
    }
  }

  &__date-input {
    width: 100%;
    padding: 8px 12px;
    border-radius: 12px;
    border: 1px solid var(--np-border, #cbd5e1);
    font-size: 12px;
    background: var(--np-surface, #fff);
    color: inherit;
  }

  &__presets {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 12px;

    button {
      border: none;
      background: var(--np-chip-bg, #f1f5f9);
      padding: 4px 10px;
      border-radius: 8px;
      font-size: 11px;
      font-weight: 600;
      cursor: pointer;
      color: var(--np-text-muted, #475569);
    }
  }

  &__presets-label {
    font-size: 11px;
    color: #94a3b8;
    font-weight: 600;
  }

  &__error {
    padding: 10px 12px;
    border-radius: 12px;
    background: #fff1f2;
    border: 1px solid #fecdd3;
    color: #be123c;
  }

  &__ok {
    padding: 12px 14px;
    border-radius: 12px;
    background: var(--np-surface-muted, #f8fafc);
    border: 1px solid var(--np-border, #e2e8f0);
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 20px;
    background: var(--np-surface-muted, #f8fafc);
    border-top: 1px solid var(--np-border, #e2e8f0);
  }

  &__btn-cancel {
    border: none;
    background: transparent;
    padding: 8px 14px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    color: var(--np-text-muted, #64748b);
  }

  &__btn-primary {
    border: none;
    background: #059669;
    color: #fff;
    padding: 10px 18px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    .v-icon {
      color: #fff !important;
    }
  }
}
</style>
